/** Angular imports */
import { DatePipe } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';

/** Component imports */
import { SelectAssetsComponent as SelectAssetsModalComponent } from '@features/playlists/components/ui';
import { Button, Modal, ThumbnailItemComponent } from '@ntv360/component-pantry';

/** Core imports */
import { AuthService } from '@core';

/** Local imports */
import { ApiDealerInformation } from '@core/services/dealer/dealer.interface';
import { UiThumbnailContent } from '@features/media-library/interfaces';
import { CreatePlaylistStepsService } from '@features/playlists/services/steps/steps.service';
import { ClientTypeName, UploaderComponent } from '@shared';
import { ContentDurationPipe } from '@shared/pipes';

@Component({
    selector: 'app-select-assets-step',
    imports: [
        Button,
        ThumbnailItemComponent,
        UploaderComponent,
        SelectAssetsModalComponent,
        Modal,
        DatePipe,
        ContentDurationPipe,
    ],
    templateUrl: './select-assets.component.html',
    styleUrl: './select-assets.component.scss',
})
export class SelectAssetsComponent implements OnInit, OnDestroy {
    /** Service that manages user authentication and session */
    private authService = inject(AuthService);

    /** Service in managing create playlist steps flow and draft data */
    private stepsService = inject(CreatePlaylistStepsService);

    /** Reference to the asset selection modal component */
    private assetSelectionModalRef = viewChild<SelectAssetsModalComponent>('assetSelectionModal');

    /** Holds the confirmed contents that have been added to the playlist */
    protected addedPlaylistContents = signal<UiThumbnailContent[]>([]);

    /** Holds the content item that is currently being previewed */
    protected previewItem = signal<UiThumbnailContent | null>(null);

    /** Controls the visibility of the add contents modal component */
    protected showAddContentsModal = signal<boolean>(false);

    /** Controls the visibility of the media uploader component */
    protected showUploader = signal<boolean>(false);

    /** Controls the visibilty of upload content button. Special for tech magic family */
    protected showForTM = signal<boolean>(false);

    /** Holds the dealer ID from the draft if there's any */
    protected draftDealerId = computed(() => {
        const dealer = this.stepsService.playlistDraft().fillInformation
            .assignToDealer as unknown as ApiDealerInformation['clientInformation'] & {
            value: string;
        };
        if (!dealer) return null;
        if (typeof dealer === 'string') return dealer;
        return dealer.value;
    });

    /** Computed property to return the details of the preview item for looping in the template */
    protected previewItemDetails = computed(() => {
        const item = this.previewItem();
        if (!item) return [];

        return [
            { label: 'Uploaded By:', value: item.ownerName },
            { label: 'Upload Date:', value: item.createdAt, type: 'date' },
            { label: 'Media Type:', value: item.type },
            { label: 'Size:', value: item.size },
            { label: 'Duration:', value: item.duration, type: 'duration' },
        ];
    });

    /** Timer for image preview sequence */
    private previewTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        effect(() => {
            const added = this.addedPlaylistContents();

            untracked(() => {
                this.stepsService.updateDraft('selectAssets', {
                    selectedContents: added,
                    addedPlaylistContents: added,
                });
            });
        });

        // Effect to handle image auto-advance
        effect(() => {
            const item = this.previewItem();
            untracked(() => {
                this.clearPreviewTimeout();
                if (item && item.type === 'image') {
                    this.previewTimeout = setTimeout(() => {
                        this.playNext();
                    }, 20000); // 20 seconds for images
                }
            });
        });
    }

    ngOnInit(): void {
        const user = this.authService.user();

        /** List of clientType that can create a playlist */
        const allowedClientTypes = [ClientTypeName.DEALER, ClientTypeName.SUPER_ADMIN];

        if (!user) throw new Error('401: Unauthorized');

        const clientTypeName = user?.data?.clientTypeName as ClientTypeName;

        if (!allowedClientTypes.includes(clientTypeName)) {
            throw new Error('401: Unauthorized');
        }

        this.initializeFromDraft();
    }

    ngOnDestroy(): void {
        this.clearPreviewTimeout();
    }

    /**
     * Initializes the component state from the draft in the steps service
     */
    private initializeFromDraft(): void {
        const draft = this.stepsService.playlistDraft().selectAssets;
        if (draft) {
            this.addedPlaylistContents.set(draft.addedPlaylistContents as UiThumbnailContent[]);
            if (this.addedPlaylistContents().length > 0) {
                this.previewItem.set(this.addedPlaylistContents()[0]);
            }
        }
    }

    /**
     * Triggers the modal to open
     */
    protected onAddContent(): void {
        this.showAddContentsModal.set(true);
    }

    /**
     * Closes the add contents modal.
     */
    protected onCloseAddContentsModal(): void {
        this.showAddContentsModal.set(false);
    }

    /**
     * Confirms the content selection from the modal and triggers the output for emitting the selected contents
     */
    protected onConfirmAddContents(): void {
        const addContentsModal = this.assetSelectionModalRef();
        if (addContentsModal) {
            addContentsModal.emitSelectedContents();
        }
    }

    /**
     * Receives the selected contents from the modal and updates the draft
     */
    protected getEmittedContents(contents: UiThumbnailContent[]): void {
        // Add unique contents to the playlist
        this.addedPlaylistContents.update((current) => {
            const currentIds = new Set(current.map((c) => c.id));
            const newItems = contents.filter((c) => !currentIds.has(c.id));
            return [...current, ...newItems];
        });

        // Set the first item as preview if none exists
        if (!this.previewItem() && this.addedPlaylistContents().length > 0) {
            this.previewItem.set(this.addedPlaylistContents()[0]);
        }

        // Close the modal
        this.showAddContentsModal.set(false);
    }

    /**
     * Handles removing a content item from both the playlist and the current selection by its ID
     *
     * @param contentId - The ID of the content to remove
     */
    protected onRemoveContent(contentId: string): void {
        this.addedPlaylistContents.update((contents) => contents.filter((c) => c.id !== contentId));

        // If the removed item was the preview item, reset preview
        if (this.previewItem()?.id === contentId) {
            this.previewItem.set(this.addedPlaylistContents()[0] || null);
        }
    }

    /**
     * Handles setting an item for preview
     *
     * @param item - The item to preview
     */
    protected setPreviewItem(item: UiThumbnailContent): void {
        this.previewItem.set(item);
    }

    /**
     * Plays the next item in the playlist
     */
    protected playNext(): void {
        const contents = this.addedPlaylistContents();
        if (contents.length === 0) return;

        const currentIndex = contents.findIndex((item) => item.id === this.previewItem()?.id);
        const nextIndex = (currentIndex + 1) % contents.length;
        this.previewItem.set(contents[nextIndex]);
    }

    /**
     * Handles video ended event
     */
    protected onVideoEnded(): void {
        this.playNext();
    }

    /**
     * Clears the current preview timeout
     */
    private clearPreviewTimeout(): void {
        if (this.previewTimeout) {
            clearTimeout(this.previewTimeout);
            this.previewTimeout = null;
        }
    }

    /**
     * Handles opening the media uploader component
     */
    protected onUploadContent(): void {
        this.showUploader.set(true);
    }
}
